import React, { useCallback, useEffect, useMemo, useState } from 'react'
import SiteMediaViewer from '../../../components/media/SiteMediaViewer'
import {
    fetchOfficePaymentReceipt,
    getReceiptDisplayName,
    getReceiptMimeType,
    triggerReceiptDownload,
} from '../../../utils/officeReceiptStore'

const formatBytes = (value) => {
    const bytes = Math.max(0, Number(value || 0))

    if (!bytes) return ''
    if (bytes < 1024) return `${bytes} Б`
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`

    return `${(bytes / (1024 * 1024))
        .toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)
        .replace('.', ',')} МБ`
}

const normalizeIndex = (index, length) => {
    if (!length) return 0
    return Math.min(length - 1, Math.max(0, Number(index) || 0))
}

export default function OfficeReceiptPreviewModal({
    open = false,
    paymentAccountId = '',
    receipts = [],
    initialIndex = 0,
    title = 'Чек',
    onClose,
}) {
    const safeReceipts = useMemo(
        () => (Array.isArray(receipts) ? receipts.filter(Boolean) : []),
        [receipts]
    )
    const [activeIndex, setActiveIndex] = useState(0)
    const [error, setError] = useState('')

    useEffect(() => {
        if (!open) return

        setActiveIndex(normalizeIndex(initialIndex, safeReceipts.length))
        setError('')
    }, [initialIndex, open, safeReceipts.length])

    const resolveReceipt = useCallback(async (receipt) => {
        if (!paymentAccountId || !receipt) {
            throw new Error('Чек не выбран')
        }

        return fetchOfficePaymentReceipt({
            paymentAccountId,
            receipt,
        })
    }, [paymentAccountId])

    const handleDownload = useCallback(async (receipt) => {
        if (!paymentAccountId || !receipt) return

        setError('')

        try {
            const result = await fetchOfficePaymentReceipt({
                paymentAccountId,
                receipt,
                download: true,
            })

            triggerReceiptDownload(result)
        } catch (downloadError) {
            setError(
                downloadError?.response?.data?.message ||
                downloadError?.response?.data?.error ||
                downloadError?.message ||
                'Не удалось скачать чек'
            )
        }
    }, [paymentAccountId])

    const handleIndexChange = useCallback((nextIndex) => {
        setError('')
        setActiveIndex(nextIndex)
    }, [])

    const getReceiptTitle = useCallback((receipt, index) => (
        getReceiptDisplayName(
            receipt,
            safeReceipts.length > 1 ? `Чек ${index + 1}` : title
        )
    ), [safeReceipts.length, title])

    const getReceiptSubtitle = useCallback((receipt) => {
        const size = formatBytes(receipt?.sizeBytes)

        return [
            'Защищённый файл',
            size,
        ].filter(Boolean).join(' · ')
    }, [])

    return (
        <SiteMediaViewer
            open={open && safeReceipts.length > 0}
            items={safeReceipts}
            activeIndex={activeIndex}
            onActiveIndexChange={handleIndexChange}
            onClose={onClose}
            resolveItem={resolveReceipt}
            getItemMimeType={getReceiptMimeType}
            getTitle={getReceiptTitle}
            getSubtitle={getReceiptSubtitle}
            getAlt={getReceiptTitle}
            onDownload={handleDownload}
            downloadDisabled={!paymentAccountId}
            loadingText="Загружаем чек…"
            emptyText="Чек недоступен"
            error={error}
        />
    )
}
